import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

/** Legend symbols — one stroke weight, the sheet's own grammar. */
function Structure() {
  return (
    <>
      <rect x="4" y="8" width="20" height="16" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M4 12 L24 12 M8 24 L8 12 M14 24 L14 12 M20 24 L20 12"
        stroke="currentColor"
        strokeWidth="1.2"
      />
      <path d="M4 8 L14 3 L24 8" stroke="currentColor" strokeWidth="1.6" fill="none" />
    </>
  )
}

function Flare() {
  return (
    <>
      <circle cx="14" cy="18" r="3" fill="currentColor" />
      <path
        d="M14 13 L14 4 M14 13 L7 6 M14 13 L21 6 M9 15 L2 11 M19 15 L26 11"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="butt"
      />
    </>
  )
}

function Junction() {
  return (
    <>
      <path d="M2 9 H11" stroke="currentColor" strokeWidth="1.6" strokeDasharray="3 2.5" />
      <path d="M17 9 H26" stroke="currentColor" strokeWidth="1.6" strokeDasharray="3 2.5" />
      <path d="M2 22 H11" stroke="currentColor" strokeWidth="1.6" strokeDasharray="3 2.5" />
      <path d="M17 22 H26" stroke="currentColor" strokeWidth="1.6" strokeDasharray="3 2.5" />
      <circle cx="14" cy="9" r="2.4" stroke="currentColor" strokeWidth="1.6" fill="none" />
      <circle cx="14" cy="22" r="2.4" stroke="currentColor" strokeWidth="1.6" fill="none" />
      <path d="M14 11.4 V19.6" stroke="currentColor" strokeWidth="1.6" />
    </>
  )
}

function Layers() {
  return (
    <>
      <rect x="4" y="6" width="20" height="4" stroke="currentColor" strokeWidth="1.6" fill="none" />
      <rect x="4" y="12" width="20" height="4" stroke="currentColor" strokeWidth="1.6" fill="none" />
      <rect x="4" y="18" width="20" height="4" stroke="currentColor" strokeWidth="1.6" fill="none" />
    </>
  )
}

function Symbol({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 28 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="h-8 w-8 shrink-0 text-ink"
      aria-hidden="true"
    >
      {children}
    </svg>
  )
}

type LegendEntry = {
  symbol: ReactNode
  title: string
  body: string
  scope: string[]
  href?: string
}

const entries: LegendEntry[] = [
  {
    symbol: <Structure />,
    title: 'Custom product engineering',
    body: 'Greenfield platforms and major product extensions — architecture, backend, frontend, and the integration layer between your existing systems.',
    scope: [
      'Architecture',
      'Backend',
      'Frontend',
      'Integration layer',
      'Deployment pipeline',
      'Handover documentation',
    ],
  },
  {
    symbol: <Flare />,
    title: 'AI-assisted delivery',
    body: 'AI applied where it saves real engineering time: scaffolding, test generation, documentation, and repetitive integration work — always under senior review.',
    scope: ['Scaffolding', 'Test generation', 'Documentation', 'Migration work'],
  },
  {
    symbol: <Junction />,
    title: 'Systems integration',
    body: 'Connect ERPs, payment rails, identity providers, and legacy APIs into coherent workflows your operators can trust day to day.',
    scope: ['ERP', 'Payment rails', 'Identity providers', 'Legacy APIs'],
  },
  {
    symbol: <Layers />,
    title: 'Compound',
    body: 'Adaptive engineering method for Cursor. Method memory updates on subscription; project memory deepens with every run — full SDLC in one field.',
    scope: ['Org license', 'Monthly skill drops', 'Four memory layers', 'Standalone product'],
    href: '/compound',
  },
]

/** Sheet legend — symbol, name, description. Dense, continuous ground. */
export function Legend() {
  return (
    <section id="legend" className="bg-sheet">
      <div className="mx-auto max-w-[100rem] px-6 py-12 lg:px-8 lg:py-16">
        <div className="mb-6 flex flex-col gap-2 border-b-2 border-ink pb-4 sm:flex-row sm:items-end sm:justify-between">
          <h2 className="section-title text-ink">What we build</h2>
          <p className="text-sm leading-relaxed text-ink-muted sm:max-w-sm sm:text-right">
            End-to-end product work for organizations that outgrew off-the-shelf tools.
          </p>
        </div>

        <div className="cell divide-y divide-rule">
          <div className="hidden grid-cols-[3rem_16rem_1fr_1fr] gap-6 bg-sheet-edge/60 px-5 py-2.5 sm:px-7 lg:grid">
            <span className="cell-label text-ink-muted">Sym</span>
            <span className="cell-label text-ink-muted">Practice</span>
            <span className="cell-label text-ink-muted">Description</span>
            <span className="cell-label text-ink-muted">In scope</span>
          </div>

          {entries.map((entry) => (
            <article
              key={entry.title}
              className="grid gap-4 px-5 py-5 sm:px-7 lg:grid-cols-[3rem_16rem_1fr_1fr] lg:items-start lg:gap-6 lg:py-6"
            >
              <Symbol>{entry.symbol}</Symbol>
              <h3 className="entry-title text-ink">
                {entry.href ? (
                  <Link to={entry.href} className="index-link text-ink">
                    {entry.title}
                  </Link>
                ) : (
                  entry.title
                )}
              </h3>
              <p className="text-[0.9375rem] leading-relaxed text-ink-muted">
                {entry.body}
              </p>
              <ul className="flex flex-wrap content-start gap-x-5 gap-y-2 lg:flex-col lg:gap-y-1.5">
                {entry.scope.map((tag) => (
                  <li
                    key={tag}
                    className="cell-label flex items-baseline gap-2 text-ink-muted"
                  >
                    <span
                      className="mt-0.5 inline-block h-px w-2.5 shrink-0 bg-overprint"
                      aria-hidden="true"
                    />
                    {tag}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}
